export type IconButtonVariant =
  "primary" | "solid" | "secondary" | "secondary-light";

/**
 * State colours come from Figma `CTAs` (`5289:2070`). As with [Button], that
 * frame draws On Click identically to Hover throughout, so `active:` mirrors
 * `hover:` and the press is told apart by scale alone.
 *
 * An edge that is present at rest changes colour; an edge that only appears
 * in a state is drawn as a ring, so arriving cannot shift the control's
 * geometry.
 */
export const ICON_BUTTON_VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  /**
   * White fill, ink glyph — no counterpart in the CTAs frame, whose Primary
   * Icon Button is this one's inverse (see `solid`). Rather than leave it
   * stateless, it inverts the way [Button]'s `solid-inverse` does: to ink
   * with a white glyph, and nothing outlining it. Disabled keeps the flat
   * tint it always had, there being no drawn state to replace it with.
   */
  primary: [
    "bg-surface text-content-primary",
    "hover:bg-brand-black hover:text-brand-white",
    "active:bg-brand-black active:text-brand-white",
    "disabled:opacity-40",
  ].join(" "),
  /**
   * Figma's Primary Icon Button (`5361:6608`) — and Third Icon Button
   * (`10151:47327`), which is drawn identically to it.
   *
   * Named `solid` because `primary` is spoken for above, and because this is
   * the same ink-fill-inverting-to-white treatment [Button]'s `solid` wears.
   */
  solid: [
    "bg-content-primary text-content-inverse",
    "hover:bg-brand-white hover:text-content-primary hover:ring-1 hover:ring-inset hover:ring-content-primary",
    "active:bg-brand-white active:text-content-primary active:ring-1 active:ring-inset active:ring-content-primary",
    "disabled:bg-transparent disabled:text-neutral2 disabled:ring-1 disabled:ring-inset disabled:ring-neutral2",
  ].join(" "),
  /**
   * The on-media variant the hero's playback control and the video modal
   * wear. Figma's CTAs frame has no icon button for dark ground, so these
   * states are `secondary-light`'s mirrored onto it rather than drawn: the
   * edge fills with white and the glyph goes to ink.
   */
  secondary: [
    "border border-tint-white-20 bg-transparent text-brand-white",
    "hover:border-transparent hover:bg-brand-white hover:text-content-primary",
    "active:border-transparent active:bg-brand-white active:text-content-primary",
    "disabled:border-transparent disabled:bg-tint-white-20 disabled:text-tint-white-50",
  ].join(" "),
  /** Figma's Secondary Icon Button (`5361:7350`), on a light surface. */
  "secondary-light": [
    "border border-tint-black-20 bg-transparent text-content-primary",
    "hover:border-transparent hover:bg-content-primary hover:text-content-inverse",
    "active:border-transparent active:bg-content-primary active:text-content-inverse",
    "disabled:border-transparent disabled:bg-neutral2 disabled:text-tint-white-50",
  ].join(" "),
};
